import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import api from '../services/api';
import '../styles/ReportModal.css';

/**
 * ReportModal - Modal for reporting notes or social links
 */
function ReportModal({
  isOpen,
  onClose,
  reportedUserId,
  contentType, // 'note' | 'social_link'
  content, // The actual note text or URL
  placeId,
  socialLinks = [], // Social links to include in report
}) {
  const [comment, setComment] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async () => {
    if (!reportedUserId || !contentType || !content || !placeId) {
      setError('Missing required information');
      return;
    }

    setSubmitting(true);
    setError(null);

    try {
      await api.submitReport({
        reportedUserId,
        contentType,
        placeId,
        content,
        comment: comment.trim() || null,
      });
      setSubmitted(true);
    } catch (err) {
      console.error('Failed to submit report:', err);
      if (err.message?.includes('already reported')) {
        setError('You have already reported this content');
      } else {
        setError('Failed to submit report. Please try again.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    setComment('');
    setSubmitted(false);
    setError(null);
    onClose();
  };

  if (!isOpen) return null;

  return createPortal(
    <div className="report-modal-overlay" onClick={handleClose}>
      <div className="report-modal" onClick={(e) => e.stopPropagation()}>
        <div className="report-modal-header">
          <h2>Report Content</h2>
          <button className="close-btn" onClick={handleClose} aria-label="Close">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
              <line x1="18" y1="6" x2="6" y2="18"/>
              <line x1="6" y1="6" x2="18" y2="18"/>
            </svg>
          </button>
        </div>

        <div className="report-modal-body">
          {submitted ? (
            <div className="report-success">
              <div className="success-icon">
                <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                  <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
                  <polyline points="22 4 12 14.01 9 11.01"/>
                </svg>
              </div>
              <h3>Report Submitted</h3>
              <p>Thank you for helping keep our community safe. We'll review this content soon.</p>
              <button className="btn-primary" onClick={handleClose}>
                Done
              </button>
            </div>
          ) : (
            <>
              <div className="reported-content">
                <label>Reported content:</label>
                <div className="content-preview">
                  {contentType === 'social_link' ? (
                    <a href={content} target="_blank" rel="noopener noreferrer" className="link-preview">
                      {content}
                    </a>
                  ) : (
                    <p className="note-preview">{content}</p>
                  )}
                  {/* Show social links if present */}
                  {socialLinks.length > 0 && (
                    <div className="social-links-preview">
                      <label>Social links:</label>
                      {socialLinks.map((link, index) => (
                        <a
                          key={index}
                          href={link.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="link-preview"
                        >
                          {link.type}: {link.url}
                        </a>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              <div className="report-reason">
                <label htmlFor="report-comment">Why are you reporting this? (optional)</label>
                <textarea
                  id="report-comment"
                  value={comment}
                  onChange={(e) => setComment(e.target.value)}
                  placeholder="Add any additional context..."
                  rows="3"
                  maxLength={500}
                />
                <span className="char-count">{comment.length}/500</span>
              </div>

              {error && (
                <div className="report-error">{error}</div>
              )}

              <div className="report-actions">
                <button className="btn-secondary" onClick={handleClose} disabled={submitting}>
                  Cancel
                </button>
                <button className="btn-primary" onClick={handleSubmit} disabled={submitting}>
                  {submitting ? 'Submitting...' : 'Submit Report'}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
}

export default ReportModal;
